import { subDays, isAfter, parseISO, differenceInMilliseconds, formatISO } from 'date-fns';
import { container, inject, injectable } from '@debatdirect/common/src/modules/container.js';
import type { Structure } from '@debatdirect/common/types/immstruct';
import { queryClient } from '@debatdirect/common/src/queryClient';

import type { AppDebate } from '../../types/debate';

import StorageService from './StorageService';

const LOCAL_STORAGE_KEY = 'verderkijken';
const DAYS_STORED = 30;

export type DebateWatched = {
  pdt: string;
  debateId: string;
  watchMoment: string;
  progress: number;
};

@injectable()
export default class ContinueWatchingService {
  private watchHistory: DebateWatched[] = [];
  readonly initialized: Promise<void>;

  constructor(@inject(StorageService) readonly storage: StorageService) {
    this.initialized = this.initialize();
  }

  private async initialize() {
    const keepFromDate = subDays(new Date(), DAYS_STORED);
    const watchHistoryStored = await this.storage.getItem<DebateWatched[]>(LOCAL_STORAGE_KEY, true);

    this.watchHistory = (watchHistoryStored || []).filter((debate) => {
      return isAfter(parseISO(debate.watchMoment), keepFromDate);
    });
  }

  private getVideoProgress(debate: AppDebate, pdt: string) {
    if (!debate.startedAt || !debate.endedAt) return 0; // Live debate
    const duration = differenceInMilliseconds(debate.endedAt, debate.startedAt);
    const position = differenceInMilliseconds(parseISO(pdt), debate.startedAt);

    return position / duration;
  }

  public wasStoredBeforeSession(id: string) {
    return !!this.watchHistory.find((debateWatched) => debateWatched.debateId === id);
  }

  public async getHistory() {
    await this.initialized;

    return this.watchHistory;
  }

  public getPdtFromWatchHistory(id: string) {
    const debateWatched = this.watchHistory.find((debateWatched) => debateWatched.debateId === id);

    return debateWatched ? debateWatched.pdt : null;
  }

  // Gets called when debate unmounts
  public saveDebate = async (debate: AppDebate | undefined) => {
    await this.initialized;

    const structure = container.get<Structure>('structure');
    const pdt = structure?.cursor(['ui', 'sync']).get('pdt') as string | undefined;
    const watchMoment = formatISO(new Date());

    if (pdt && debate) {
      const progress = this.getVideoProgress(debate, pdt);
      const debateWatched = { pdt, debateId: debate.id, watchMoment, progress };
      const foundIndex = this.watchHistory.findIndex((watchedDebate) => watchedDebate.debateId === debate.id);

      if (foundIndex >= 0) {
        this.watchHistory = this.watchHistory.map((item, index) => (index === foundIndex ? debateWatched : item));
      } else {
        this.watchHistory = this.watchHistory.concat([debateWatched]);
      }
    }
    this.storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.watchHistory));

    queryClient.invalidateQueries({ queryKey: ['watchHistory'], exact: true });
  };

  public removeDebate = async (debateId: string) => {
    await this.initialized;

    this.watchHistory = this.watchHistory.filter((debate) => debate.debateId !== debateId);
    this.storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.watchHistory));

    queryClient.invalidateQueries({ queryKey: ['watchHistory'], exact: true });
  };
}
